import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { LoginResponse } from '../api/auth'
import { formsApi, type FormDetail, type FormRequest } from '../api/forms'
import { departmentsApi, type Department } from '../api/team'
import {
  FormGrid,
  Button, ButtonLink, Callout, Card, CardContent, CardHeader, LoadingState, OverflowMenu, Page, PageHeader, Select, TextField, useConfirm,
} from '../components/ui'

/**
 * Same Admin-tier check established by `Dashboard.tsx` / `UsersPage.tsx`, most recently reused by
 * `CompanyDetailPage.tsx` (TASK-0035) — `auth.role === 'Administrator' || auth.role ===
 * 'EqualAdmin'`, the two role values `PolicyNames.Admin` grants server-side. Reused verbatim here
 * (not reinvented) to gate visibility of the Delete button: `DELETE /api/forms/{id}` is
 * `[Authorize(PolicyNames.Admin)]` on `FormsController`.
 */
function isAdmin(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

/**
 * `Create`/`Update` require the server-side `CanManageForms` category permission (Admin bypasses)
 * — unlike Admin-or-not, the frontend has no claim that tells it this in advance, so the UI never
 * pre-hides the Save action for this; a 403 is caught and turned into a clear message instead.
 */
function friendlyError(err: unknown, fallback: string): string {
  const msg = err instanceof Error ? err.message : String(err)
  if (msg.toLowerCase().includes('forbidden') || msg.includes('403')) {
    return 'Permesso negato: la gestione dei form richiede il permesso "Gestione Form" (CanManageForms) sulla propria categoria utente. Contatta un amministratore.'
  }
  return msg || fallback
}

interface Props {
  auth: LoginResponse
}

export function FormDetailPage({ auth }: Props) {
  const confirm = useConfirm()
  const { id: idParam } = useParams<{ id: string }>()
  const isCreate = idParam === undefined
  const id = isCreate ? undefined : parseInt(idParam, 10)
  const navigate = useNavigate()

  const [departments, setDepartments] = useState<Department[]>([])
  const [existing, setExisting] = useState<FormDetail | null>(null)

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [fields, setFields] = useState('')

  const [loading, setLoading] = useState(!isCreate)
  const [notFound, setNotFound] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedMsg, setSavedMsg] = useState<string | null>(null)

  useEffect(() => {
    void departmentsApi.list().then(setDepartments).catch(() => { /* select options only, not fatal */ })
  }, [])

  useEffect(() => {
    if (isCreate || id === undefined) return
    setLoading(true)
    setNotFound(false)
    setError(null)
    formsApi
      .get(id)
      .then(f => {
        setExisting(f)
        setName(f.name)
        setSlug(f.slug)
        setDepartmentId(f.departmentId != null ? String(f.departmentId) : '')
        setFields(f.fields ?? '')
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e)
        if (msg.includes('404') || msg.toLowerCase().includes('not found')) setNotFound(true)
        else setError(msg || 'Errore nel caricamento del form')
      })
      .finally(() => setLoading(false))
  }, [isCreate, id])

  async function handleSave() {
    if (!name.trim() || !slug.trim()) {
      setError('Nome e slug sono obbligatori.')
      return
    }
    setSaving(true)
    setError(null)
    setSavedMsg(null)
    try {
      if (isCreate) {
        const body: FormRequest = {
          name: name.trim(),
          slug: slug.trim(),
          departmentId: departmentId ? parseInt(departmentId, 10) : null,
          fields: fields.trim() ? fields : null,
        }
        const res = await formsApi.create(body)
        navigate(`/forms/${res.id}`)
        return
      }
      if (id === undefined) return
      // The backend's Update only persists `Fields` (FormsController.Update calls only
      // form.UpdateFields(req.Fields) — see forms.ts's FormRequest doc comment). name/slug/
      // departmentId are echoed back unchanged purely to satisfy [ApiController] model binding
      // (they're non-nullable on the DTO); they are not editable in this UI once a form exists.
      await formsApi.update(id, {
        name,
        slug,
        departmentId: departmentId ? parseInt(departmentId, 10) : null,
        fields: fields.trim() ? fields : null,
      })
      const refreshed = await formsApi.get(id)
      setExisting(refreshed)
      setFields(refreshed.fields ?? '')
      setSavedMsg('Campi salvati.')
    } catch (err) {
      setError(friendlyError(err, 'Errore durante il salvataggio'))
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (id === undefined) return
    const ok = await confirm({
      title: 'Eliminare il form',
      message: `Eliminare il form "${name}"? L'operazione non è reversibile.`,
      confirmLabel: 'Elimina',
      destructive: true,
    })
    if (!ok) return
    setDeleting(true)
    setError(null)
    try {
      await formsApi.remove(id)
      navigate('/forms')
    } catch (err) {
      setError(friendlyError(err, "Errore durante l'eliminazione"))
      setDeleting(false)
    }
  }

  if (loading) {
    return <LoadingState />
  }

  if (notFound) {
    return (
      <Callout tone="warning">
        Form non trovato. <Link to="/forms">Torna alla lista</Link>
      </Callout>
    )
  }

  return (
    <Page>
      <PageHeader
        title={isCreate ? 'Nuovo form' : name}
        icon="dynamic_form"
        back={{ to: '/forms', label: 'Form' }}
        actions={
          !isCreate && isAdmin(auth) ? (
            <OverflowMenu
              aria-label="Altre azioni"
              items={[{ label: deleting ? 'Eliminazione...' : 'Elimina', icon: 'delete', onSelect: () => void handleDelete(), disabled: deleting }]}
            />
          ) : undefined
        }
      />

      {error && <Callout tone="error">{error}</Callout>}
      {savedMsg && <Callout tone="success">{savedMsg}</Callout>}

      <Card variant="outlined">
        <CardHeader
          title="Dati form"
          {...(!isCreate
            ? { subtitle: '— nome, slug e reparto non sono modificabili dopo la creazione' }
            : {})}
        />
        <CardContent>
          <FormGrid>
            <div>
              <TextField
                id="fdp-name"
                type="text"
                label="Nome *"
                value={name}
                disabled={!isCreate}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div>
              <TextField
                id="fdp-slug"
                type="text"
                label="Slug *"
                value={slug}
                disabled={!isCreate}
                onChange={e => setSlug(e.target.value)}
              />
            </div>
          </FormGrid>
          <FormGrid className="mt-3">
            <div>
              <Select
                id="fdp-department"
                label="Reparto"
                value={departmentId}
                disabled={!isCreate}
                onChange={e => setDepartmentId(e.target.value)}
              >
                <option value="">Nessun reparto</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </Select>
            </div>
          </FormGrid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader
          title="Campi"
          subtitle="— JSON libero, trattato come testo (nessun editor strutturato in questa vista)"
        />
        <CardContent>
          <TextField
            id="fdp-fields"
            multiline
            rows={10}
            label="Campi"
            labelHidden
            className="font-monospace"
            placeholder='[{"name": "email", "type": "text", "label": "Email"}]'
            value={fields}
            onChange={e => setFields(e.target.value)}
          />
        </CardContent>
      </Card>

      {!isCreate && existing && (
        <div className="text-muted md-typescale-body-small">
          Creato il {new Date(existing.createdAt).toLocaleDateString('it-IT')}
          {existing.updatedAt && ` · Aggiornato il ${new Date(existing.updatedAt).toLocaleDateString('it-IT')}`}
          {' · '}
          {existing.isActive ? 'Attivo' : 'Inattivo'}
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex gap-2">
          <Button
            variant="filled"
            {...(saving ? {} : { icon: 'save' })}
            loading={saving}
            disabled={saving}
            onClick={() => void handleSave()}
          >
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
          <ButtonLink to="/forms" variant="text">Annulla</ButtonLink>
        </div>

      </div>
    </Page>
  )
}
